import { spacing, Text } from '@scality/core-ui';
import { LegendItem, LegendShape } from '@scality/core-ui/dist/components/charts/legend/ChartLegend';
import { useChartLegend } from '@scality/core-ui/dist/next';
import { chartColors } from '@scality/core-ui/dist/style/theme';
import type React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';
import type { LegendInstance } from '../../../portal/instanceMarker';
import { InstanceBadge } from '../../InstanceBadge';

type ChartInstanceLegendProps = {
  shape: 'line' | 'rectangle';
  direction?: 'horizontal' | 'vertical';
  size?: 'Basic' | 'Smaller';
  color?: 'textPrimary' | 'textSecondary';
  /** Keyed by the series name the chart knows, which is what the legend lists. */
  instances: Record<string, LegendInstance>;
};

const Legend = styled.div<{ $direction: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${({ $direction }) => ($direction === 'horizontal' ? 'row' : 'column')};
  gap: ${({ $direction }) => ($direction === 'horizontal' ? spacing.f16 : spacing.f8)};
  flex-wrap: wrap;
`;

/**
 * `ChartLegend` with a deployment shown as an `InstanceBadge` rather than as text — the same
 * reading a Zendesk ticket gives it, tooltip with the raw id included. core-ui's own legend takes
 * `labelMap`, which is strings only, so the item is rebuilt here from its exported parts.
 */
export const ChartInstanceLegend: React.FC<ChartInstanceLegendProps> = ({
  shape,
  direction = 'horizontal',
  size = 'Basic',
  color = 'textPrimary',
  instances,
}) => {
  const {
    listResources,
    getColor,
    getLabel,
    isSelected,
    addSelectedResource,
    removeSelectedResource,
    selectAllResources,
    selectOnlyResource,
    isOnlyOneSelected,
  } = useChartLegend();

  // core-ui's own click policy: plain click isolates a series (or restores all when it is already
  // the only one), modifier-click adds and removes.
  const handleClick = useCallback(
    (resource: string, event: React.MouseEvent) => {
      const selected = isSelected(resource);
      if (event.metaKey || event.ctrlKey) {
        if (!selected) addSelectedResource(resource);
        else if (isOnlyOneSelected()) selectAllResources();
        else removeSelectedResource(resource);
      } else if (selected && isOnlyOneSelected()) {
        selectAllResources();
      } else {
        selectOnlyResource(resource);
      }
    },
    [
      isSelected,
      addSelectedResource,
      removeSelectedResource,
      selectAllResources,
      selectOnlyResource,
      isOnlyOneSelected,
    ],
  );

  return (
    <Legend $direction={direction}>
      {listResources().map((resource) => {
        const selected = isSelected(resource);
        const instance = instances[resource];

        return (
          <LegendItem
            key={resource}
            $selected={selected}
            aria-label={`${resource} ${selected ? 'selected' : 'not selected'}`}
            onClick={(event: React.MouseEvent) => handleClick(resource, event)}
          >
            <LegendShape $color={getColor(resource)} $shape={shape} $chartColors={chartColors} />
            {instance ? (
              <>
                <InstanceBadge id={instance.id} />
                {instance.rest && (
                  <Text variant={size} color={color}>
                    {instance.rest}
                  </Text>
                )}
              </>
            ) : (
              <Text variant={size} color={color}>
                {getLabel(resource)}
              </Text>
            )}
          </LegendItem>
        );
      })}
    </Legend>
  );
};
